import React, { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AnimatePresence } from 'framer-motion';
import { Button, Container, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers';
import { NextSeo } from 'next-seo';
import { UserCircle } from '@olo-web/assets/icons/UserCircle.ui';
import { SpotOnO } from '@olo-web/assets/icons/SpotOnO.ui';
import { MotionBox } from '@atoms/motion';
import { useModalDispatch, useScreenSizeState } from '@olo-web/client-state';
import { BackToMenuButton } from '@olo-web/components/atoms/BackToMenuButton';
import { GoToEditAccountButton } from '@olo-web/components/atoms/GoToEditAccountButton';
import { ContentCard } from '@olo-web/components/atoms/ContentCard';
import { Banner } from '@olo-web/components/atoms/Banner';
import { useMerchantPath } from '@domain/merchants/hooks/useMerchantPath';
import { Promotions } from '@olo-web/components/molecules/Promotions';
import { Rewards } from '@olo-web/components/molecules/Rewards';
import { ContactInfoView } from '@olo-web/components/molecules/ContactInfoView';
import { Footer } from '@olo-web/components/molecules/Footer';
import { ContactInfoForm } from '@olo-web/components/organisms/Forms/ContactInfoForm';
import { DiscountType } from '@olo-web/domain/orders/types';
import { useCustomer, useLoyalty, useUpdateProfile } from '@olo-web/domain/customer';
import { EModalTypes } from '@olo-web/types/enums';
import { contactInformationValidationSchema } from '@olo-web/utils/common/validationSchema';
import { AlertListener } from '@organisms/Alerts';
import { useOffersHelpers } from '@olo-web/components/organisms/Modals/ApplyOffersModal/hooks/useOffersHelpers';
import { useRouter } from 'next/router';
import { AccountBottomButtons } from './AccountBottomButtons';
import { AccountNavBar } from './AccountNavBar';
import { getDiscountType } from '@olo-web/domain/customer/functions';
import { useIsDineIn } from '@olo-web/utils/common/hooks';
import { LoyaltyQRCodeButton } from './LoyaltyQRCodeButton';

type TSpots = {
  spots: string;
};

const variants = {
  visible: {
    opacity: 1,
    height: 'auto',
  },
  hidden: {
    opacity: 0,
    height: 0,
  },
};

const Spots = ({ spots = '0' }: TSpots) => (
  <HStack>
    <Text>Your balance:</Text>
    <Text fontWeight="semibold">{Math.floor(+spots)} Spots</Text>
    <SpotOnO color="loyalty.400" />
  </HStack>
);

export const Account = () => {
  const { isSmDown } = useScreenSizeState();
  const { data: customer } = useCustomer();
  const { data: loyalty } = useLoyalty();
  const modalDispatch = useModalDispatch();
  const { availableOffers, unavailableOffers } = useOffersHelpers();
  const path = useMerchantPath();
  const router = useRouter();
  const onSuccess = () => {
    router.push(`${path}/account/`);
  };
  const isDineIn = useIsDineIn();

  const { mutateAsync: updateProfile, error, reset } = useUpdateProfile({ onSuccess });

  const isEdit = router.route.includes('edit');

  const rewards = useMemo(
    () =>
      [...availableOffers, ...unavailableOffers].filter(
        (offer) => getDiscountType(offer) === DiscountType.REWARD
      ),
    [availableOffers, unavailableOffers]
  );

  const promos = useMemo(
    () =>
      [...availableOffers, ...unavailableOffers].filter(
        (offer) =>
          getDiscountType(offer) === DiscountType.PROMO_CODE ||
          getDiscountType(offer) === DiscountType.DEAL
      ),
    [availableOffers, unavailableOffers]
  );

  const openModal = (key: EModalTypes) => {
    modalDispatch({
      type: 'OPEN_MODAL',
      payload: {
        modalKey: key,
      },
    });
  };

  const handleSendForm = async (data: ICustomer) => {
    try {
      const { phoneNo: phone, ...resData } = data;

      await updateProfile({ ...resData, phone });
    } catch (err) {
      console.warn(err);
    }
  };

  const formMethods = useForm({
    resolver: yupResolver(contactInformationValidationSchema),
  });

  return (
    <Flex align="stretch" flexDir="column" bg="base.50" minH="100vh" w="100vw">
      <NextSeo title="My account" />
      <AccountNavBar />
      <BackToMenuButton mt={3} includeQueryParam={!isDineIn} />
      <Container
        role="main"
        maxW="container.xl"
        flex={1}
        pt={{ base: 0, md: 6 }}
        px={{ base: 0, md: 42 }}
      >
        {!customer ? (
          <VStack id="main-content" spacing={6}>
            <Text as="h1" textStyle="h3">
              Sign in to view your account
            </Text>
            <Button
              size="lg"
              variant="solid"
              colorScheme="primary"
              onClick={() => openModal(EModalTypes.LOGIN)}
              rightIcon={<UserCircle h={5} w={5} ml={1} />}
            >
              Sign in
            </Button>
          </VStack>
        ) : (
          <VStack
            w={isSmDown ? '100%' : '800px'}
            align="stretch"
            mx="auto"
            spacing={6}
            id="main-content"
          >
            <Text
              as="h1"
              textStyle="h1"
              ml={{ base: 4, sm: 6, md: 0 }}
              fontSize={{ base: 24, md: 30 }}
            >
              My account
            </Text>
            <ContentCard
              title="Contact information"
              rightTitleAdornment={<GoToEditAccountButton isEdit={isEdit} />}
            >
              <AnimatePresence initial={false}>
                {isEdit ? (
                  <MotionBox
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                    key="form-customer"
                    variants={variants}
                  >
                    <FormProvider {...formMethods}>
                      <ContactInfoForm
                        firstName={customer?.firstName}
                        lastName={customer?.lastName}
                        email={customer?.email}
                        phoneNo={customer?.phone || customer?.phoneNo}
                      />
                    </FormProvider>
                  </MotionBox>
                ) : (
                  <ContactInfoView
                    key="info-customer"
                    customer={{ ...customer, phoneNo: customer?.phone || '' }}
                    hideTitle
                  />
                )}
              </AnimatePresence>

              {!!isEdit && (
                <Button
                  variant="solid"
                  colorScheme="primary"
                  onClick={formMethods.handleSubmit(handleSendForm)}
                  fontSize="lg"
                  type="submit"
                  alignSelf="flex-start"
                >
                  Update Information
                </Button>
              )}
              {error && (
                <Banner
                  status="error"
                  error={error}
                  title="Unable to login"
                  onClose={reset}
                  mb={2}
                />
              )}
            </ContentCard>
            {!isDineIn && (
              <>
                {!!rewards.length && (
                  <ContentCard
                    title="Rewards"
                    rightTitleAdornment={<Spots spots={loyalty?.availableSpots} />}
                  >
                    <Rewards />
                  </ContentCard>
                )}

                {!!promos.length && (
                  <ContentCard title="Your promotions">
                    <Promotions />
                  </ContentCard>
                )}
              </>
            )}
            <LoyaltyQRCodeButton />
            <AccountBottomButtons />
          </VStack>
        )}
      </Container>
      <AlertListener />
      <Footer />
    </Flex>
  );
};
